import ReduxTanStackSync from 'src/new_arch/services/ReduxTanStackSync';
import { objectToFormData } from 'src/lib/requestUtils';
import { WORKSPACES_URL } from 'src/config/urls';
import Api from 'src/services/Api';
import WorkspaceRole from 'src/entities/workspaceRole';
import { AddOnPreviewJSON, NonMemberJSON, WorkspaceInvitation, WorkspaceJSON, WorkspaceMemberJSON } from '../types';

type MarkOnboardingAsClosedParams = {
	workspaceID: number
};

export type WorkspaceProperties = {
	name: string;
	logo: string | File | null;
};

export default class WorkspaceSystem {
	private api: Api;
	private sync: ReduxTanStackSync;

	constructor( { api, sync }: { api: Api, sync: ReduxTanStackSync } ) {
		this.api = api;
		this.sync = sync;
	}

	async markOnboardingAsClosed( { workspaceID }: MarkOnboardingAsClosedParams ): Promise<void> {
		const url = `${this.baseURLFor( { workspaceID } )}/mark_onboarding_as_closed`;
		await ( this.api.patch( url ) as Promise<void> );

		this.sync.markWorkspaceOnboardingAsClosed( workspaceID );
	}

	async markEditorAccessModalAsSeen( { workspaceID }: { workspaceID: number } ): Promise<void> {
		const url = `${this.baseURLFor( { workspaceID } )}/mark_editor_access_modal_as_seen`;
		await ( this.api.patch( url ) as Promise<void> );
	}

	private baseURLFor( { workspaceID }: { workspaceID: number } ) {
		return `${WORKSPACES_URL}/${workspaceID}`;
	}

	async fetchWorkspaceMembers( { workspaceID }: { workspaceID: number } ): Promise<WorkspaceMemberJSON[]> {
		const url = `${this.baseURLFor( { workspaceID } )}/members`;
		const response = await ( this.api.get( url ) as Promise<{ response: WorkspaceMemberJSON[] }> );
		return response.response;
	}

	async updateWorkspace( {
		workspaceID, properties
	}: { workspaceID: number, properties: Partial<WorkspaceProperties> } ): Promise<WorkspaceJSON> {
		const url = `${this.baseURLFor( { workspaceID } )}`;
		const data = objectToFormData( properties );

		const response = await ( this.api.patch(
			url, data, { 'Accept': 'application/json, */*', 'Content-Type': undefined }
		) as Promise<{ response: WorkspaceJSON }> );

		const propertiesToSync = {
			name: response.response.name,
			logo: response.response.logo
		};

		this.sync.updateWorkspaceProperties( workspaceID, propertiesToSync );

		return response.response;
	}

	async fetchNonMembers( { workspaceID }: { workspaceID: number } ): Promise<NonMemberJSON[]> {
		const url = `${this.baseURLFor( { workspaceID } )}/non_members`;
		const response = await ( this.api.get( url ) as Promise<{ response: NonMemberJSON[] }> );
		return response.response;
	}

	async assignSeatToNonMember( {
		workspaceID, email
	}: { workspaceID: number; email: string } ): Promise<void> {
		const url = `${this.baseURLFor( { workspaceID } )}/members/invite`;
		await ( this.api.post( url, {
			invites: [ { email, role: WorkspaceRole.admin().id } ]
		} ) as Promise<void> );
	}

	async requestSeatForNonMember( {
		workspaceID, nonMemberID
	}: { workspaceID: number; nonMemberID: number } ): Promise<void> {
		const url = `${this.baseURLFor( { workspaceID } )}/non_members/${nonMemberID}/request_seat`;
		await ( this.api.post( url, {} ) as Promise<void> );
	}

	async deleteWorkspaceLogo( { workspaceID }: { workspaceID: number } ): Promise<void> {
		const url = `${this.baseURLFor( { workspaceID } )}/logo`;
		await ( this.api.delete( url ) as Promise<void> );

		this.sync.updateWorkspaceProperties( workspaceID, { logo: null } );
	}

	async fetchMemberAddOnPreview( {
		workspaceID, quantity
	}: { workspaceID: number, quantity: number } ): Promise<AddOnPreviewJSON> {
		const url = `${this.baseURLFor( { workspaceID } )}/members/add_on_preview?quantity=${quantity}`;
		const response = await ( this.api.get( url ) as Promise<AddOnPreviewJSON> );
		return response;
	}

	async addWorkspaceAddOn( {
		workspaceID, addOnPriceID, quantity
	}: { workspaceID: number, addOnPriceID: number, quantity: number } ): Promise<WorkspaceJSON> {
		const url = `${this.baseURLFor( { workspaceID } )}/add_ons`;
		const response = await ( this.api.post(
			url, { add_on_price_id: addOnPriceID, quantity }
		) as Promise<{ response: WorkspaceJSON }> );

		this.sync.workspaceAddOnBought( response.response );

		return response.response;
	}

	async removeWorkspaceAddOn( {
		workspaceID, addOnPriceID, quantity
	}: { workspaceID: number, addOnPriceID: number, quantity: number } ): Promise<WorkspaceJSON> {
		const url = `${this.baseURLFor( { workspaceID } )}/add_ons`;
		const body = { add_on_price_id: addOnPriceID, quantity } as unknown as undefined;
		const response = await ( this.api.delete( url, body ) as Promise<{ response: WorkspaceJSON }> );

		this.sync.workspaceAddOnRemoved( response.response );

		return response.response;
	}

	async inviteWorkspaceMembers( {
		workspaceID, invites
	}: { workspaceID: number, invites: WorkspaceInvitation[] } ): Promise<WorkspaceJSON> {
		const url = `${this.baseURLFor( { workspaceID } )}/members/invite`;
		const response = await ( this.api.post(
			url, { invites }
		) as Promise<{ response: WorkspaceJSON }> );

		this.sync.workspaceMembersInvited( response.response );

		return response.response;
	}
}
